import {
  PANEL_FACTORS,
  HR_FACTORS,
  WORK_CATEGORIES_ENGINEERING,
} from '@/lib/reportConstants';

// =========================================================================
// Hardcoded sample data — mirrors the real client-report payload shape.
// All numbers, transcripts, MCQ questions, AI summaries below are FIXTURES.
// =========================================================================
export const SAMPLE_VIDEO_URL = 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4';
export const SAMPLE_PANEL_VIDEO_URL = 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerEscapes.mp4';

export const DEMO = {
  candidate: {
    id: 'demo-cand-001',
    full_name: 'Priya Ramachandran',
    role_label: 'Senior Backend Engineer',
    department_label: 'Platform Engineering',
    resume: {
      summary:
        'Priya is a backend engineer with 7 years of experience designing high-throughput, low-latency distributed systems. ' +
        'She has led the rebuild of two payment platforms (one processing 4M txns/day), specialises in Go and Python, ' +
        'and is comfortable owning service reliability end-to-end — from on-call to capacity planning to incident postmortems.',
      years_experience: 7,
      key_skills: ['Go', 'Python', 'Kafka', 'PostgreSQL', 'Kubernetes', 'gRPC', 'Distributed Systems', 'Observability'],
    },
  },

  videobot: {
    eval: {
      verdict: 'Strong',
      eval_summary:
        'Communicates with structure, depth, and concrete examples across all recorded answers. ' +
        'Delivery is confident with clear trade-off framing and relevant technical detail.',
      strengths: ['structured answers', 'concrete examples', 'confident delivery'],
      weaknesses: ['occasional pacing on longer prompts'],
    },
    responses: [
      {
        question_id: 'q1', client_curated: true, duration_seconds: 92,
        question_text: 'Walk us through a system you designed end-to-end and the trade-offs you made.',
        video_url: SAMPLE_VIDEO_URL, overall_score: 4.3,
        eval_summary: 'Structured walkthrough of a payment reconciliation pipeline with clear trade-off framing on consistency versus throughput.',
        transcript: 'I designed the payment reconciliation pipeline at my last company. We had three upstream sources of truth and a daily settlement deadline... [sample transcript truncated for demo]',
      },
      {
        question_id: 'q2', client_curated: true, duration_seconds: 84,
        question_text: 'Describe a time you had to debug a production incident under pressure.',
        video_url: SAMPLE_VIDEO_URL, overall_score: 4.0,
        eval_summary: 'Calm incident narrative with concrete mitigation steps and postmortem learnings under time pressure.',
        transcript: 'It was Black Friday at 2am. Our checkout latency spiked from 80ms to 6 seconds. I led the incident response...',
      },
      {
        question_id: 'q3', client_curated: true, duration_seconds: 110,
        question_text: 'How do you approach mentoring a junior engineer who is struggling?',
        video_url: SAMPLE_VIDEO_URL, overall_score: 3.8,
        eval_summary: 'Empathetic mentoring approach distinguishing technical gaps from confidence issues with actionable coaching steps.',
        transcript: 'I start by understanding whether it is a technical gap or a confidence gap...',
      },
      {
        question_id: 'q4', client_curated: true, duration_seconds: 76,
        question_text: 'Tell us about a technical decision you got wrong, and what you learned.',
        video_url: SAMPLE_VIDEO_URL, overall_score: 4.1,
        eval_summary: 'Honest reflection on a DynamoDB versus relational store misstep with clear lessons on workload fit.',
        transcript: 'I picked DynamoDB for a workload that was actually heavily relational...',
      },
    ],
  },

  mcq: {
    result: {
      score_pct: 90,
      correct: 27,
      total_questions: 30,
      percentile: 88,
      pool_size: 240,
      difficulty_breakdown: [
        { level: 'Easy',   correct: 10, total: 10 },
        { level: 'Medium', correct: 12, total: 14 },
        { level: 'Hard',   correct: 5,  total: 6 },
      ],
    },
    // Sprint 12 demo refinement — categorisation summary surfaced in the
    // hero MCQ block; full per-question detail moved into a modal.
    categories: [
      { name: 'Quantitative Aptitude', total: 8, correct: 7, sample_topics: ['medium', 'easy'] },
      { name: 'Logic', total: 6, correct: 6, sample_topics: ['medium'] },
      { name: 'Reasoning', total: 6, correct: 5, sample_topics: ['medium', 'hard'] },
      { name: 'Data Interpretation', total: 4, correct: 3, sample_topics: ['charts'] },
      { name: 'Computer & IT Fundamentals', total: 4, correct: 4, sample_topics: ['easy'] },
      { name: 'Verbal Comprehension', total: 2, correct: 2, sample_topics: ['easy'] },
    ],
    items: [
      {
        question_id: 'm1',
        question_text: 'The difference between a two-digit number and the number obtained by interchanging its digits is 36. What is the difference between the two digits?',
        options: ['3', '4', '5', 'Cannot be determined'],
        selected_index: 1, correct_index: 1, is_correct: true, topic: 'Quantitative Aptitude', difficulty: 'Medium',
      },
      {
        question_id: 'm2',
        question_text: 'If all Zips are Zaps, and some Zaps are Zops, which statement must be true?',
        options: ['All Zips are Zops', 'Some Zips may be Zops', 'No Zips are Zops', 'All Zops are Zips'],
        selected_index: 1, correct_index: 1, is_correct: true, topic: 'Logic', difficulty: 'Medium',
      },
      {
        question_id: 'm3',
        question_text: 'Which of these best describes the purpose of an API?',
        options: ['Styling a web page', 'Letting two software systems communicate', 'Storing data on disk', 'Compiling source code'],
        selected_index: 1, correct_index: 1, is_correct: true, topic: 'Computer & IT Fundamentals', difficulty: 'Easy',
      },
    ],
  },

  tech_assessment: {
    result: {
      score_pct: 90,
      correct: 27,
      total_questions: 30,
      difficulty_breakdown: [
        { level: 'Easy', correct: 10, total: 10 },
        { level: 'Medium', correct: 12, total: 14 },
        { level: 'Hard', correct: 5, total: 6 },
      ],
    },
    categories: WORK_CATEGORIES_ENGINEERING.map((name, i) => ({
      name,
      total: [6, 5, 5, 5, 5, 4][i],
      correct: [6, 4, 5, 4, 4, 4][i],
      sample_topics: ['role-specific'],
    })),
    items: [
      {
        question_id: 't1',
        question_text: 'Which HTTP method is idempotent and safe to retry without side effects?',
        options: ['POST', 'PATCH', 'GET', 'DELETE'],
        selected_index: 2, correct_index: 2, is_correct: true, topic: 'Technology Foundation & Architecture', difficulty: 'Medium',
      },
    ],
  },

  panels: [
    {
      id: 'p1',
      label: 'Panel Evaluation',
      code_editor_enabled: false,
      recording_url: SAMPLE_PANEL_VIDEO_URL,
      transcript_text:
        'Interviewer: Walk us through how you would design a global URL shortener.\n' +
        'Priya: I would start by clarifying read-vs-write ratios and the 99th percentile latency SLA we are targeting.',
      ai_summary: 'Session covered systems design with solid latency framing; weaker on capacity planning detail under probing.',
      factor_scores: [
        { name: 'Technology Foundation', score: 3.5 },
        { name: 'Design & Architecture', score: 2.5 },
        { name: 'Solutioning Mindset', score: 2.5 },
        { name: 'Engineering Process Know-how', score: 3.0 },
        { name: 'AI-Driven Development', score: 3.5 },
        { name: 'Infosec & Data Modeling', score: 2.0 },
      ],
      human_overall_score: 2.8,
      ai_report: {
        overall_score: 2.8,
        overall_recommendation: 'Neutral',
        summary_narrative: {
          overall_assessment: 'Session covered systems design with solid latency framing; weaker on capacity planning detail under probing.',
        },
        spider: PANEL_FACTORS.map((dimension, i) => ({
          dimension,
          score: [3.5, 2.5, 2.5, 3.0, 3.5, 2.0][i],
          fullMark: 5,
        })),
      },
    },
  ],

  hr_evaluation: {
    overall_score: 3.75,
    factors: HR_FACTORS.map((name, i) => ({
      name,
      score: [4.0, 3.5, 4.0, 3.0, 3.5, 4.5][i],
    })),
    final_call: 'recommend',
  },

  recommendation: {
    status: 'completed',
    final_call: 'recommend',
    final_call_label: 'Recommend',
    subtitle: 'Composite 73% · Strong fundamentals with specific, addressable gaps',
    grounded_in_data:
      'IQ Assessment 90% and Work Assessment 90% show solid quantitative, logic, and MERN fundamentals. '
      + 'Panel Evaluation (2.8/5) scored Design & Architecture, Solutioning Mindset at 2.5/5 and Infosec & Data Modeling at 2.0/5 — the three lowest-rated factors. '
      + 'HR Evaluation (3.75/5) shows strong Cultural Fit (4.5/5) and Team Collaboration (4.0/5).',
    watch_outs:
      'Video Bot average of 2.0/5 across 7 responses. Panel-rated Leadership Potential 3.0/5 and Solutioning Mindset 2.5/5 '
      + 'indicate limited exposure to ambiguous, open-ended problem framing — expected at this experience level and addressable with structured mentorship.',
    learning_plan_intro:
      'Built directly from the lowest-rated factors above (Design & Architecture, Solutioning Mindset, Infosec & Data Modeling, Leadership Potential).',
    learning_plan: {
      days_1_30: [
        'Pair with a senior engineer mentor for code reviews focused on architecture patterns.',
        'Complete internal system-design fundamentals module.',
        'Shadow two sprint-planning sessions to observe solutioning approach.',
      ],
      days_31_60: [
        'Own one well-scoped feature end-to-end with an architecture review checkpoint.',
        'Complete the Infosec & Data Modeling foundations course.',
        'Weekly 1:1 with manager on structured problem-framing.',
      ],
      days_61_90: [
        'Lead a design review for a medium-complexity feature, mentor sign-off required.',
        'Present a security / data-modeling review for a project component.',
        '90-day check-in benchmarked against Work Assessment & Panel Evaluation scores.',
      ],
    },
    next_steps: [
      'Extend offer for Trainee / Junior Software Engineer track.',
      'Assign an onboarding mentor before day 1, briefed on this learning plan.',
      'Schedule 30/60/90-day check-ins against the plan above.',
    ],
    composite_pct: 73,
  },

  offer: null,

  final_report: {
    narrative: '',
    readiness: { evaluated_stages: 5, pending_stages: 0, is_complete: true, total_stages: 5 },
  },

  company_resume: {
    payload: {
      header: {
        full_name: 'Priya Ramachandran',
        headline: 'Senior Backend Engineer · Distributed Systems',
        location: 'Bengaluru, India',
      },
      summary:
        'Backend engineer with 7 years building high-throughput distributed systems in Go and Python. ' +
        'Led payment platform rebuilds processing millions of daily transactions.',
      key_highlights: [
        'Backend engineer with hands-on depth in Go, Python, Kafka, and PostgreSQL.',
        'Led payment platform rebuilds processing millions of daily transactions.',
        'Owned on-call for checkout latency SLOs end-to-end.',
      ],
      skills: {
        languages: ['Go', 'Python'],
        platforms: ['Kafka', 'PostgreSQL', 'Kubernetes'],
        practices: ['Distributed Systems', 'gRPC'],
      },
      experience: [
        {
          company: 'FinStack Payments',
          role: 'Senior Backend Engineer',
          start: '2021',
          end: 'Present',
          bullets: ['Rebuilt reconciliation pipeline for 4M txns/day', 'Owned on-call for checkout latency SLOs'],
        },
      ],
      certifications: [
        { name: 'AWS Solutions Architect Professional', issued_by: 'Amazon Web Services', year: '2024' },
        { name: 'AWS Cloud Foundations', issued_by: 'Amazon Web Services', year: '2023' },
      ],
      education: [
        { degree: 'B.Tech Computer Science', institution: 'IIT Bombay', year: '2017' },
      ],
    },
    generated_at: '2026-06-10T10:00:00Z',
    template: {
      brand_color: '#0078D4',
      show_photo: false,
      show_contact_details: false,
      sections: [
        { key: 'summary', label: 'Profile Summary', enabled: true, order: 1 },
        { key: 'key_highlights', label: 'Key Highlights', enabled: true, order: 2 },
        { key: 'skills', label: 'Core Skills', enabled: true, order: 3 },
        { key: 'experience', label: 'Past Companies & Experience', enabled: true, order: 4 },
        { key: 'certifications', label: 'Certifications', enabled: true, order: 5 },
      ],
    },
  },

  link: {
    expires_at: '2026-06-28T12:00:00Z',
    issued_at:  '2026-06-14T12:00:00Z',
    expiry_days: 14,
    hr_cover_note:
      'Sharing Priya for the Senior Backend Engineer role on your Platform team. She has cleared all four ' +
      'assessment stages with strong consistency across reviewers. Available for an offer conversation this week.',
    cfg: { transcript_visible: true, code_editor_visible: true, mcq_percentile_min_pool: 100 },
  },
};
